import { useState } from "react"
import { IconCopyFill18 } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Shortcut, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { OriginBadge } from "@/components/thread-list/origin-badge"
import { SlackThreadOriginBadge } from "@/components/thread-list/slack-origin-icon"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadContext } from "../../thread-context"

const ORIGIN_ICON_CLASS =
	"h-4 w-4 flex-none !text-secondary [&:not(.lucide)]:!fill-gray-500 [&:not(.lucide)]:dark:!fill-gray-400"

export function ThreadTitle() {
	const zero = useZero()
	const { thread } = useThreadContext()
	const { role } = useRole()

	const title = thread.title ?? ""
	const [titleValue, setTitleValue] = useState(title)

	const isOverLimit = titleValue.length >= 512

	const copyThreadNumber = async () => {
		if (thread.number == null) return
		const value = `#${thread.number}`
		await navigator.clipboard.writeText(value)
		toast.info({ title: `Copied ${value}`, icon: IconCopyFill18 })
	}

	useShortcut("mod+period", () => void copyThreadNumber(), {
		preventDefault: true,
		enabled: thread.number != null,
	})

	return (
		<div className="flex w-full min-w-0 flex-row items-center gap-2">
			{thread.origin === "SLACK_CONNECT" ? (
				<SlackThreadOriginBadge
					slackChannelId={thread.slackChannelId}
					createdAt={thread.createdAt}
					updatedAt={thread.updatedAt}
					className={ORIGIN_ICON_CLASS}
				/>
			) : (
				<OriginBadge
					origin={thread.origin}
					customChannel={thread.customChannel}
					createdAt={thread.createdAt}
					updatedAt={thread.updatedAt}
					className={ORIGIN_ICON_CLASS}
				/>
			)}
			<div className="flex min-w-0 flex-1 flex-col">
				<div className="flex min-w-0 flex-row items-center gap-1.5">
					{thread.number != null && (
						<Tooltip
							label={
								<span className="inline-flex items-center gap-2">
									Copy thread number
									<span className="flex gap-1">
										<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
										<Shortcut>{ShortcutSymbols["."]}</Shortcut>
									</span>
								</span>
							}
						>
							<button
								type="button"
								data-testid="thread-number"
								className="-mx-1 flex-none cursor-default rounded-sm px-1 text-[0.9375rem] font-semibold leading-5 tracking-tight text-primary hover:bg-gray-150 dark:hover:bg-gray-800"
								onClick={() => void copyThreadNumber()}
							>
								#{thread.number}
							</button>
						</Tooltip>
					)}
					<input
						data-testid="thread-title"
						className={cn(
							"min-w-0 flex-1 truncate rounded-sm border-none bg-transparent p-0 text-[0.9375rem] font-semibold leading-5 tracking-tight text-primary outline-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed",
							{
								"select-text": role === "VIEWER",
							},
						)}
						placeholder="Thread title"
						value={titleValue}
						maxLength={512}
						readOnly={role === "VIEWER"}
						onChange={(e) => {
							setTitleValue(e.target.value)
						}}
						onBlur={async () => {
							if (role !== "VIEWER") {
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											title: titleValue,
										}),
									),
								)
							}
						}}
						onKeyDown={async (e) => {
							if (e.key === "Enter" && role !== "VIEWER") {
								e.preventDefault()
								// React clears currentTarget once the handler awaits.
								const input = e.currentTarget
								await mutateOk(
									zero.mutate(
										mutators.threads.update({
											id: thread.id,
											title: titleValue,
										}),
									),
								)
								input.blur()
							}
						}}
					/>
				</div>
				{isOverLimit && (
					<span className="text-xs text-red-600 dark:text-red-400">
						Title cannot be longer than 512 characters.
					</span>
				)}
			</div>
		</div>
	)
}
